import { baseApi } from '../../../data/baseApi'

export type NpCatalogImportArgs = {
  sheetUrl: string
  dryRun: boolean
  /** Returned by the dry run; required to run the real import (`dryRun: false`). */
  fingerprint?: string
}

export type NpCatalogImportCounts = {
  rows_read: number
  rows_skipped: number
  rows_with_errors: number
  measures_created: number
  measures_updated: number
  measures_unchanged: number
  parent_links_changed: number
  batteries_created: number
  batteries_reactivated: number
  batteries_deprecated: number
  battery_measures_added: number
  battery_measures_removed: number
}

export type NpCatalogImportError = {
  row: number | null
  internal_id: string | null
  column: string | null
  message: string
}

export type NpCatalogImportWarning = {
  code: string
  message: string
  rows: number[]
}

export type NpCatalogImportBattery = {
  action: string
  battery_id: string | null
  name: string
  condition: string
  modality: string
  age_range_min: number | null
  age_range_max: number | null
  measure_count: number
  /** Measure internal_ids (TM_ID) added to / removed from the battery. */
  measures_added: string[]
  measures_removed: string[]
}

export type NpCatalogImportMeasureChange = { from: unknown; to: unknown }

export type NpCatalogImportMeasure = { internal_id: string; name: string; row: number }

export type NpCatalogImportUpdatedMeasure = NpCatalogImportMeasure & {
  changes: Record<string, NpCatalogImportMeasureChange>
}

export type NpCatalogImportMeasureNotInSheet = { internal_id: string; name: string; status: string }

// Summary returned by the backend for both the dry run and the confirmed import.
export type NpCatalogImportResponse = {
  dry_run: boolean
  applied: boolean
  can_apply: boolean
  fingerprint: string
  source?: { spreadsheet_id: string; gid: string; tab: string }
  counts: NpCatalogImportCounts
  measures: {
    created: NpCatalogImportMeasure[]
    updated: NpCatalogImportUpdatedMeasure[]
    not_in_sheet: NpCatalogImportMeasureNotInSheet[]
  }
  batteries: { changed: NpCatalogImportBattery[]; deprecated: { battery_id: string; name: string }[] }
  errors: NpCatalogImportError[]
  warnings: NpCatalogImportWarning[]
}

// TODO(open question): which role should authorize this endpoint? It likely needs to be the NP admin /
// provider admin role rather than ROLE_internal; to be settled with backend. ProviderPortalAPI's LT proxy sets
// X-Lyra-Roles from the user's JWT roles, so no role header is sent from here.

const npCatalogImportApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    importNpCatalog: builder.mutation<NpCatalogImportResponse, NpCatalogImportArgs>({
      query: ({ sheetUrl, dryRun, fingerprint }) => ({
        method: 'post',
        url: '/lt/v1/internal/np-testing/catalog-imports',
        data: { sheet_url: sheetUrl, dry_run: dryRun, ...(fingerprint ? { fingerprint } : {}) },
        // The admin page renders the backend's error details (and a rejected summary) itself.
        showAlert: false,
      }),
    }),
  }),
})

export const { useImportNpCatalogMutation } = npCatalogImportApi
export default npCatalogImportApi
